import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download } from "lucide-react";
import {
  exportSchedules,
  SCHEDULE_LABELS,
  type ScheduleKey,
  type ExportFormat,
  type ScheduleExportData,
  type ExportMeta,
} from "@/lib/scheduleExport";

interface Props {
  data: ScheduleExportData;
  meta: ExportMeta;
  mode: 'advisor' | 'client';
}

export function ScheduleExportPanel({ data, meta, mode }: Props) {
  const availableKeys: ScheduleKey[] = mode === 'advisor'
    ? ['policyLedger', 'amortization', 'commitment']
    : ['amortization', 'commitment'];

  const [selected, setSelected] = useState<Set<ScheduleKey>>(new Set(availableKeys));
  const [format, setFormat] = useState<ExportFormat>('xlsx');
  const [busy, setBusy] = useState(false);

  const toggle = (key: ScheduleKey) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleExport = async () => {
    const picks = availableKeys.filter(k => selected.has(k));
    if (picks.length === 0) return;
    setBusy(true);
    try {
      await exportSchedules(picks, format, data, meta);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mb-3 border-emerald-200/60 dark:border-emerald-900/40 bg-emerald-50/40 dark:bg-emerald-950/20">
      <CardContent className="py-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Download className="h-4 w-4 text-emerald-700 dark:text-emerald-400" />
          Export Loan Schedules
        </div>
        <p className="text-xs text-muted-foreground">
          Choose any combination of schedules and a format. Excel produces one workbook with a tab per schedule; CSV downloads as a zip when more than one is selected.
        </p>
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          {availableKeys.map(key => (
            <div key={key} className="flex items-center gap-2">
              <Checkbox
                id={`export-${key}`}
                checked={selected.has(key)}
                onCheckedChange={() => toggle(key)}
              />
              <Label htmlFor={`export-${key}`} className="cursor-pointer text-sm">
                {SCHEDULE_LABELS[key]}
              </Label>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Format</Label>
            <Select value={format} onValueChange={(v) => setFormat(v as ExportFormat)}>
              <SelectTrigger className="w-44 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
                <SelectItem value="csv">CSV</SelectItem>
                <SelectItem value="pdf">PDF (print)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={handleExport}
            disabled={busy || selected.size === 0}
            className="gap-2"
            size="sm"
          >
            <Download className="h-4 w-4" />
            {busy ? 'Exporting…' : 'Export'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}